import React, { useState, useEffect, useRef } from "react";
import ReactFlow, {
  Controls,
  Background,
  ReactFlowProvider,
  Handle,
} from "react-flow-renderer";
import dagre from "dagre";
import Tree from "react-d3-tree";
import {
  Container,
  Form,
  Button,
  Dropdown,
  DropdownButton,
  FormCheck,
  Tabs,
  Tab,
  Modal,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import "./JsonEditor.css";
import { logUserActivity, logError, logFeedbackToFirestore } from "../firebase"; // Import logging functions
import { auth } from "../firebase"; // Import Firebase auth
import FlowChart from "./FlowChart";
import { db } from "../firebase"; // Import Firestore database

const AGENT_FUNCTIONS = {
  detect_roach_motel_design: "Roach Motel",
  detect_bait_and_switch_deceptive_design: "Bait and Switch",
  detect_visual_interference_design: "Visual Interference",
  detect_misdirection_deceptive_design: "Misdirection",
  detect_privacy_zuckering_deceptive_design: "Privacy Zuckering",
  detect_trick_wording_deceptive_design: "Trick Wording",
  detect_sneaking_deceptive_design: "Sneaking",
  detect_preselection_deceptive_design: "Preselection",
  detect_nagging_deceptive_design: "Nagging",
  detect_hidden_subscription_deceptive_design: "Hidden Subscription",
  detect_hidden_costs_deceptive_design: "Hidden Costs",
  detect_forced_action_deceptive_design: "Forced Action",
  detect_fake_social_proof_deceptive_design: "Fake Social Proof",
  detect_fake_scarcity_deceptive_design: "Fake Scarcity",
  detect_disguised_ads_design: "Disguised Ads",
  detect_confirm_shaming_deceptive_design: "Confirm Shaming",
  detect_comparison_prevention_deceptive_design: "Comparison Prevention",
  detect_obstruction_deceptive_design: "Obstruction",
  meta_deceptive_design: "I Don't Know",
};

const JsonEditor = ({
  jsonData: initialJsonData,
  onSave,
  selectedFolder,
  navigateToReport,
  onUpdateJsonData,
}) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const isNodeSelected = selectedNode !== null;
  const prevSelectedNodeID = useRef(null);
  const iframeRef = useRef(null);
  const [widgetReady, setWidgetReady] = useState(false); // Track if widget is ready
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [formData, setFormData] = useState({
    ID: "",
    action: "",
    image_url: "",
    description: "",
    validation: "",
    agents: {},
    suggestion: "",
    feedback_image_url: "",
  });
  const [jsonData, setJsonData] = useState(initialJsonData);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedAgentKey, setSelectedAgentKey] = useState(null);
  const [activeTab, setActiveTab] = useState("editing");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isReportReady, setIsReportReady] = useState(false);
  const [useCurrentDescription, setUseCurrentDescription] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [userJourneyData, setUserJourneyData] = useState(null);
  const [userJourneyError, setUserJourneyError] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [updateMessage, setUpdateMessage] = useState("");
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    if (activeTab === "userJourney" && jsonData && jsonData.name) {
      fetchUserJourney();
    }
  }, [activeTab, jsonData]);

  useEffect(() => {
    console.log("Loaded JSON Data:", initialJsonData);
    const cleanedJsonData = removeNodesWithoutImages(initialJsonData);
    if (cleanedJsonData) {
      setJsonData(cleanedJsonData);
    } else {
      console.error("No valid nodes found in JSON data.");
    }
    setLoading(false); // Set loading to false after processing
  }, [initialJsonData]);

  const removeNodesWithoutImages = (node) => {
    if (!node) return null;

    // If the node has children, recursively process them
    if (node.children) {
      node.children = node.children
        .map(removeNodesWithoutImages)
        .filter((child) => child !== null); // Filter out null children
    }

    // Return the node if it has an image_url or has children with image_url
    return node.image_url || (node.children && node.children.length > 0) ? node : null;
  };

  useEffect(() => {
    if (selectedNode) {
      const findParentUrl = (node, targetID) => {
        if (node.children) {
          for (const child of node.children) {
            if (child.ID === targetID) {
              return node.name; // Found the parent node, return its URL (name)
            }
            // Search in the child's subtree
            const foundUrl = findParentUrl(child, targetID);
            if (foundUrl) return foundUrl;
          }
        }
        return null;
      };
      const parentUrl = findParentUrl(jsonData, selectedNode.ID);

      setFormData({
        ID: selectedNode.ID || "",
        action: selectedNode.action || "",
        image_url: selectedNode.image_url || "",
        description: selectedNode.description || "",
        validation: selectedNode.validation || "",
        agents: selectedNode.agents || {},
        suggestion: selectedNode.suggestion || "",
        feedback_image_url: selectedNode.feedback_image_url || "",
        url: parentUrl || "N/A",
      });
      setSelectedAgents(Object.keys(selectedNode.agents || {}));
      if (prevSelectedNodeID.current !== selectedNode.ID) {
        setSaveMessage(""); // Reset save message when selecting a new node
      }
      prevSelectedNodeID.current = selectedNode.ID; // Update the ref
    }
  }, [selectedNode]);

  useEffect(() => {
    if (activeTab === "userJourney") {
      fetchStructuredUserJourney();
    }
  }, [activeTab]);

  const fetchUserJourney = async () => {
    if (!jsonData || !jsonData.name) {
      console.error("jsonData or jsonData.name is undefined");
      setUserJourneyError("No folder name available in jsonData");
      return;
    }

    const folderName = jsonData.name;
    console.log("Fetching user journey for folder:", folderName);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/get_user_journey`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ folder_name: folderName }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.error) {
          setUserJourneyError(data.error);
          setUserJourneyData(null);
        } else {
          setUserJourneyData(data);
          setUserJourneyError(null);
          const { nodes, edges } = processUserJourneyData(data);
          setNodes(nodes);
          setEdges(edges);
        }
      } else {
        const errorData = await response.json();
        setUserJourneyError(errorData.error || "User journey not available");
        setUserJourneyData(null);
      }
    } catch (error) {
      console.error("Error fetching user journey:", error);
      setUserJourneyError("User journey not available");
      setUserJourneyData(null);
    }
  };

  // Dagre layout setup
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  const nodeWidth = 240; // Match the width in CustomNode
  const nodeHeight = 80; // Estimated height

  const getLayoutedElements = (nodes, edges, direction = "TB") => {
    const isHorizontal = direction === "LR";
    dagreGraph.setGraph({ rankdir: direction });

    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    nodes.forEach((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      node.targetPosition = isHorizontal ? "left" : "top";
      node.sourcePosition = isHorizontal ? "right" : "bottom";

      node.position = {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      };

      // Optionally lock the position to prevent dragging
      node.draggable = false;

      return node;
    });

    return { nodes, edges };
  };

  const processUserJourneyData = (data) => {
    const nodes = [];
    const edges = [];
    const urlToNodeId = {};
    let nodeId = 0;

    // Remove truncation function
    // const truncateLabel = (label, maxLength = 30) => {
    //   return label.length > maxLength ? `${label.substring(0, maxLength)}...` : label;
    // };

    // Add startUrl as node
    const startUrl = data.startUrl;
    urlToNodeId[startUrl] = `${nodeId}`;
    nodes.push({
      id: `${nodeId}`,
      data: { label: startUrl }, // Use full URL
      position: { x: 0, y: 0 },
      type: "customNode", // Use custom node
    });
    nodeId++;

    // Process each step in the path
    data.path.forEach((step) => {
      const fromUrl = step.from;
      const toUrl = step.to;
      const action = step.action;

      // Add nodes if they don't exist
      if (!(fromUrl in urlToNodeId)) {
        urlToNodeId[fromUrl] = `${nodeId}`;
        nodes.push({
          id: `${nodeId}`,
          data: { label: fromUrl }, // Use full URL
          position: { x: 0, y: 0 },
          type: "customNode",
        });
        nodeId++;
      }
      if (!(toUrl in urlToNodeId)) {
        urlToNodeId[toUrl] = `${nodeId}`;
        nodes.push({
          id: `${nodeId}`,
          data: { label: toUrl }, // Use full URL
          position: { x: 0, y: 0 },
          type: "customNode",
        });
        nodeId++;
      }

      // Add edge with action as label
      edges.push({
        id: `e${urlToNodeId[fromUrl]}-${urlToNodeId[toUrl]}`,
        source: urlToNodeId[fromUrl],
        target: urlToNodeId[toUrl],
        label: action,
        animated: true,
        style: { stroke: "#0077b6" },
        labelStyle: { fill: "#0077b6", fontWeight: 700 },
      });
    });

    // Apply layout
    const layoutedElements = getLayoutedElements(nodes, edges, "TB");

    return layoutedElements;
  };

  const fetchStructuredUserJourney = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/get-structure-user-journey`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_journey_data: userJourneyData, // Pass user journey data
          }),
        }
      );

      if (response.ok) {
        const structuredData = await response.json();
        console.log("Structured User Journey Data:", structuredData); // Log the data
        setUserJourneyData(structuredData);
        setUserJourneyError(null);
      } else {
        const errorData = await response.json();
        // setUserJourneyError(
        //   errorData.error || "Failed to fetch structured user journey"
        // );
        setUserJourneyData(null);
      }
    } catch (error) {
      console.error("Error fetching structured user journey:", error);
      // setUserJourneyError("Failed to fetch structured user journey");
      setUserJourneyData(null);
    }
  };

  // Updated CustomNode component
  const CustomNode = ({ data }) => {
    return (
      <div
        style={{
          padding: 10,
          border: "1px solid #999",
          borderRadius: 5,
          backgroundColor: "#fff",
          width: 240, // Fixed width
          wordWrap: "break-word",
          overflowWrap: "break-word",
        }}
      >
        <Handle type="target" position="top" />
        <div style={{ textAlign: "center" }}>{data.label}</div>
        <Handle type="source" position="bottom" />
      </div>
    );
  };

  const nodeTypes = {
    customNode: CustomNode,
  };

  // Function to send JSON data to iframe on button click
  const sendToEmbed = () => {
    const jsonCrackEmbed = iframeRef.current;

    const json = JSON.stringify(jsonData); // Use the initialJsonData passed to the component

    const options = {
      theme: "light", // You can change this to "dark" if needed
      direction: "RIGHT", // Change to UP, DOWN, LEFT, or RIGHT
    };

    // Ensure iframe is available and postMessage to the widget
    if (jsonCrackEmbed && jsonCrackEmbed.contentWindow) {
      jsonCrackEmbed.contentWindow.postMessage({ json, options }, "*");
      console.log("JSON data sent to the iframe:", json); // Debugging log
    } else {
      console.warn("Iframe is not loaded or not available");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleClick = (nodeData) => {
    if (!nodeData.children) {
      setSelectedNode(nodeData);
      fetchAgentRecommendations(nodeData);
    } else {
      setSelectedNode(null); // Deselect if a parent node is clicked
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const updateJsonData = (updatedNode) => {
    const updateChildren = (children) => {
      return children.map((child) => {
        if (child.ID === updatedNode.ID) {
          return { ...child, ...updatedNode };
        } else if (child.children) {
          return { ...child, children: updateChildren(child.children) };
        }
        return child;
      });
    };

    setJsonData((prevData) => ({
      ...prevData,
      children: updateChildren(prevData.children),
    }));
  };

  // const renderCustomNode = ({ nodeDatum, toggleNode }) => {
  //   const isSelected = selectedNode && selectedNode.ID === nodeDatum.ID;
  //   return (
  //     <g>
  //       <circle
  //         r={15}
  //         fill={isSelected ? "#ffcc00" : "#fff"} // Yellow when selected, white otherwise
  //         stroke="#333"
  //         strokeWidth={isSelected ? 3 : 1}
  //         onClick={() => {
  //           handleClick(nodeDatum);
  //           if (nodeDatum.children) {
  //             toggleNode();
  //           }
  //         }}
  //         style={{ cursor: "pointer" }}
  //       />
  //       <text
  //         fill="black"
  //         x={20}
  //         dy={5}
  //         onClick={() => {
  //           handleClick(nodeDatum);
  //           if (nodeDatum.children) {
  //             toggleNode();
  //           }
  //         }}
  //         style={{ fontSize: "10px", fontWeight: "normal", cursor: "pointer" }} // Change fontWeight to normal
  //       >
  //         {nodeDatum.name
  //           ? nodeDatum.name.substring(0, 30) + "..."
  //           : nodeDatum.ID}{" "}
  //         //..
  //       </text>
  //     </g>
  //   );
  // };

  const renderNodeLabel = (nodeData) => (
    <div
      className={`node-label ${
        selectedNode && selectedNode.ID === nodeData.ID ? "selected" : ""
      }`}
      style={{
        maxWidth: "150px", // Set a max width for the node label
        whiteSpace: "nowrap", // Prevents text wrapping
        overflow: "hidden", // Ensures text is cut off
        textOverflow: "ellipsis", // Shows ellipsis (...) when text is too long
      }}
    >
      {nodeData.name || nodeData.ID}
    </div>
  );

  const handleSaveNode = () => {
    if (selectedNode) {
      const updatedNode = {
        ...selectedNode,
        ...formData,
      };

      updateJsonData(updatedNode);
      setSelectedNode(updatedNode);
      console.log("Node saved successfully", updatedNode);
      setSaveMessage("Saved Successfully");
      // Log activity: User saved a node
      logUserActivity(
        auth.currentUser.uid,
        auth.currentUser.displayName,
        `Saved node with ID: ${updatedNode.ID}`
      );
    }
  };

  useEffect(() => {
    if (saveMessage) {
      const timer = setTimeout(() => {
        setSaveMessage(""); // Clear the message after 5 seconds
      }, 10000); // Adjust the time (5000ms = 5 seconds)

      return () => clearTimeout(timer); // Cleanup the timeout on component unmount
    }
  }, [saveMessage]);

  const handleSaveAll = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/save-json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            jsonData: jsonData,
            folder_name: jsonData.name,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      console.log("All changes saved successfully");
      onUpdateJsonData(jsonData);
      // Automatically download the JSON file
      const jsonString = JSON.stringify(jsonData, null, 2); // Pretty print with 2 spaces
      const fileName = `${jsonData.name}_results.json`;

      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      // Clean up by revoking the object URL
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 0);
      setIsReportReady(true);

      // Log activity: User saved all changes
      logUserActivity(
        auth.currentUser.uid,
        auth.currentUser.displayName,
        `Saved all changes for folder: ${jsonData.name}`
      );
    } catch (error) {
      console.error("Error saving all changes:", error);
      setErrorMessage(`Error saving all changes: ${error.message}`);
      // Log error
      logError(
        `Error saving all changes for folder: ${jsonData.name}, error: ${error.message}`,
        auth.currentUser.uid,
        auth.currentUser.displayName
      );
    }
  };

  const handleAgentSelect = (agentKey) => {
    setSelectedAgents((prevSelectedAgents) => {
      if (prevSelectedAgents.includes(agentKey)) {
        // Remove agent if already selected
        return prevSelectedAgents.filter((agent) => agent !== agentKey);
      } else {
        // Add agent if not selected
        return [...prevSelectedAgents, agentKey];
      }
    });
  };

  const handleAgentViewChange = (agentKey) => {
    setSelectedAgentKey(agentKey);

    if (agentKey === "All") {
      // Combine all agent responses into one text block
      const allResponses = Object.values(formData.agents)
        .map((agent) => agent.result)
        .join("\n\n"); // Joining with a new line separator

      const allValidations = Object.values(formData.agents)
        .map((agent) => agent.validation)
        .join("\n\n"); // Joining with a new line separator

      // Update the form data to show all responses
      setFormData((prevFormData) => ({
        ...prevFormData,
        agents: {
          ...prevFormData.agents,
          All: {
            result: allResponses,
            validation: allValidations,
          },
        },
      }));
    }
  };

  const handleAgentInputChange = (e) => {
    const { value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      agents: {
        ...prevFormData.agents,
        [selectedAgentKey]: {
          result: value,
          validation: prevFormData.agents[selectedAgentKey]?.validation || "",
        },
      },
    }));
  };

  const handleRegenerateDescription = async () => {
    setIsGenerating(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/regenerate-description`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            image_url: formData.image_url,
            file_selected: jsonData.name, // Pass the selected folder (file ID)
            image_id: formData.ID, //
          }),
        }
      );
      const data = await response.json();
      setFormData((prevFormData) => ({
        ...prevFormData,
        description: data.description,
      }));
      // Log activity: User regenerated description
      logUserActivity(
        auth.currentUser.uid,
        auth.currentUser.displayName,
        `Generated description for image ID: ${formData.ID}`
      );
    } catch (error) {
      console.error("Error regenerating description:", error);
      setErrorMessage(`Error regenerating description: ${error.message}`);
      // Log error
      logError(
        `Error regenerating description for image ID: ${formData.ID}, error: ${error.message}`,
        auth.currentUser.uid,
        auth.currentUser.displayName
      );
    }
    setIsGenerating(false);
  };

  const handleRegenerateValidation = async () => {
    setIsGenerating(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/regenerate-validation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            image_url: formData.image_url,
            description: formData.description,
            file_selected: jsonData.name, // Pass the selected folder (file ID)
            image_id: formData.ID, //
          }),
        }
      );
      const data = await response.json();
      setFormData((prevFormData) => ({
        ...prevFormData,
        validation: data.validation,
      }));
      // Log activity: User regenerated validation
      logUserActivity(
        auth.currentUser.uid,
        auth.currentUser.displayName,
        `Generated validation for image ID: ${formData.ID}`
      );
    } catch (error) {
      console.error("Error regenerating validation:", error);
      setErrorMessage(`Error regenerating validation: ${error.message}`);
      // Log error
      logError(
        `Error regenerating validation for image ID: ${formData.ID}, error: ${error.message}`,
        auth.currentUser.uid,
        auth.currentUser.displayName
      );
    }
    setIsGenerating(false);
  };

  const handleGenerateAgentResponses = async () => {
    setIsGenerating(true);
    try {
      if (!userJourneyData) {
        await fetchUserJourney();
      }
      // fetchUserJourney();
      const descriptionToUse = useCurrentDescription
        ? formData.description
        : "The given image is a screenshot of a webpage";

      const validationToUse = useCurrentDescription
        ? formData.validation
        : "The given image is a screenshot of a webpage to detect the deceptive design";

      // Function to process agents in batches
      const processAgentBatch = async (agentsBatch) => {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/run-agent`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              agents: agentsBatch,
              public_url: formData.image_url,
              description: descriptionToUse,
              validation: validationToUse,
              file_selected: jsonData.name, // Pass the selected folder (file ID)
              image_id: formData.ID, //
              user_journey_data: userJourneyData, // Pass user journey data
            }),
          }
        );
        return response.json();
      };

      const updatedAgents = { ...formData.agents };
      // Process agents in batches of 3
      for (let i = 0; i < selectedAgents.length; i += 3) {
        const agentsBatch = selectedAgents.slice(i, i + 3);
        const data = await processAgentBatch(agentsBatch);

        agentsBatch.forEach((agentKey) => {
          updatedAgents[agentKey] = {
            result: data.agent_responses[agentKey],
            validation: data.validation_result,
          };
        });
      }

      setFormData((prevFormData) => ({
        ...prevFormData,
        agents: updatedAgents,
      }));

      // Log activity: User generated agent responses
      logUserActivity(
        auth.currentUser.uid,
        auth.currentUser.displayName,
        `Generated agent responses for image ID: ${formData.ID}`
      );

      if (selectedAgents.length > 0) {
        setSelectedAgentKey(selectedAgents[0]);
      }
      // handleSaveNode();
    } catch (error) {
      console.error("Error generating agent responses:", error);
      setErrorMessage(`Error generating agent responses: ${error.message}`);
      // Log error
      logError(
        `Error generating agent responses for image ID: ${formData.ID}, error: ${error.message}`,
        auth.currentUser.uid,
        auth.currentUser.displayName
      );
    }
    setIsGenerating(false);
  };

  const handleGenerateSuggestions = async () => {
    setIsGenerating(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/generate-suggestions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            agent_results: formData.agents,
            description: formData.description,
            file_selected: jsonData.name, // Pass the selected folder (file ID)
            image_id: formData.ID, //
          }),
        }
      );
      const data = await response.json();
      setFormData((prevFormData) => ({
        ...prevFormData,
        suggestion: data.suggestions,
      }));
      // Log activity: User generated suggestions
      logUserActivity(
        auth.currentUser.uid,
        auth.currentUser.displayName,
        `Generated suggestions for image ID: ${formData.ID}`
      );

      // Generate feedback image after suggestions
      // await handleGenerateFeedbackImage(data.suggestions);
    } catch (error) {
      console.error("Error generating suggestions:", error);
      setErrorMessage(`Error generating suggestions: ${error.message}`);
      // Log error
      logError(
        `Error generating suggestions for image ID: ${formData.ID}, error: ${error.message}`,
        auth.currentUser.uid,
        auth.currentUser.displayName
      );
    }
    setIsGenerating(false);
    // handleSaveNode();
  };

  const handleGenerateFeedbackImage = async (suggestions) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/generate-feedback-image`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            suggestions: suggestions,
            description: formData.description,
            image_url: formData.image_url,
          }),
        }
      );
      const data = await response.json();
      setFormData((prevFormData) => ({
        ...prevFormData,
        feedback_image_url: data.feedback_image_url,
      }));
    } catch (error) {
      console.error("Error generating feedback image:", error);
      setErrorMessage(`Error generating feedback image: ${error.message}`);
    }
  };

  const handleGenerateAllDescriptionsAndValidations = async () => {
    setIsGenerating(true);

    const traverseAndGenerate = async (node) => {
      if (!node.children) {
        try {
          // Generate Description
          const descriptionResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/regenerate-description`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                image_url: node.image_url,
                file_selected: jsonData.name, // Pass the selected folder (file ID)
                image_id: node.ID, // Use node.ID
              }),
            }
          );
          const descriptionData = await descriptionResponse.json();

          // Generate Validation
          const validationResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/regenerate-validation`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                image_url: node.image_url,
                description: descriptionData.description,
                file_selected: jsonData.name, // Pass the selected folder (file ID)
                image_id: node.ID, // Use node.ID
              }),
            }
          );
          const validationData = await validationResponse.json();

          const updatedNode = {
            ...node,
            description: descriptionData.description,
            validation: validationData.validation,
          };

          updateJsonData(updatedNode);
          // setSelectedNode(updatedNode); // Do not update UI for each node
        } catch (error) {
          console.error(`Error processing node ${node.ID}:`, error);
          setErrorMessage(`Error processing node ${node.ID}: ${error.message}`);
          // You can add logic to visually indicate the error on the node if desired
        }
      } else if (node.children) {
        for (const child of node.children) {
          await traverseAndGenerate(child);
        }
      }
    };

    await traverseAndGenerate(jsonData);
    setIsGenerating(false);
    console.log(
      "Finished generating descriptions and validations for all images."
    );
  };

  const fetchAgentRecommendations = async (node) => {
    if (node.agents_recommendation) {
      // Use existing recommendations if available
      setSelectedAgents(node.agents_recommendation);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/get-agent-recommendation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            public_url: node.image_url,
            file_selected: jsonData.name,
            image_id: node.ID,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const recommendedAgents = data.agents || [];

        // Update the node with recommendations
        const updatedNode = {
          ...node,
          agents_recommendation: recommendedAgents, // Store the recommended agents in the node
        };

        // Update the JSON structure
        updateJsonData(updatedNode);

        // Set the selected node again to force re-render and reflect changes
        setSelectedNode(updatedNode);

        // Set the recommended agents in the dropdown
        setSelectedAgents(recommendedAgents);
      } else {
        console.error("Failed to fetch agent recommendations");
      }
    } catch (error) {
      console.error("Error fetching agent recommendations:", error);
    }
  };

  // Helper function to truncate the URL
  const truncateURL = (url, maxLength = 30) => {
    if (!url) return "";
    return url.length > maxLength ? `${url.substring(0, maxLength)}...` : url;
  };

  const handleSubmitFeedback = async () => {
    if (!selectedAgentKey || !formData.feedback) {
      setErrorMessage("Please select an agent and provide feedback.");
      return;
    }

    const agentName = AGENT_FUNCTIONS[selectedAgentKey];
    const feedback = formData.feedback;
    const feedbackText = formData.feedbackDetails || "NA"; // Set feedback text to 'NA' if not provided

    // Show updating message
    setUpdateMessage(`Updating ${agentName} response...`);

    try {
      // If feedback is "Perfect", skip the regeneration process
      if (feedback === "Perfect") {
        // Log the feedback to Firestore
        await logFeedbackToFirestore({
          image_id: formData.ID,
          image_url: formData.image_url,
          agent_name: agentName,
          feedback_selected: feedback,
          feedback_text: feedbackText,
          timestamp: new Date(),
          user_name: auth.currentUser.displayName,
          filename: jsonData.name,
          agent_old_value: formData.agents[selectedAgentKey].result,
          agent_new_value: formData.agents[selectedAgentKey].result, // No change in response
        });
        setUpdateMessage(`${agentName} feedback logged as perfect.`);
        return; // Exit the function
      }

      // For other feedback options, call the API to regenerate the response
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/regenerate-agent`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            public_url: formData.image_url,
            agent: selectedAgentKey,
            feedback: feedbackText,
            description: formData.description,
            validation: formData.validation,
            file_selected: jsonData.name,
            image_id: formData.ID,
            user_journey_data: userJourneyData,
          }),
        }
      );

      const data = await response.json();
      setUpdateMessage(`${agentName} response is updated.`);

      // Update the agent's response in the state
      setFormData((prevFormData) => ({
        ...prevFormData,
        agents: {
          ...prevFormData.agents,
          [selectedAgentKey]: {
            ...prevFormData.agents[selectedAgentKey],
            result: data.updated_response,
          },
        },
      }));

      // Log feedback to Firestore
      await logFeedbackToFirestore({
        image_id: formData.ID,
        image_url: formData.image_url,
        agent_name: agentName,
        feedback_selected: feedback,
        feedback_text: feedbackText,
        timestamp: new Date(),
        user_name: auth.currentUser.displayName,
        filename: jsonData.name,
        agent_old_value: formData.agents[selectedAgentKey].result,
        agent_new_value: data.updated_response,
      });

      // Save the node after updating
      handleSaveNode();
    } catch (error) {
      console.error("Error updating agent response:", error);
      setErrorMessage(`Error updating agent response: ${error.message}`);
    } finally {
      // Reset the update message after a short delay if necessary
      setTimeout(() => {
        setUpdateMessage("");
      }, 5000); // Clear message after 5 seconds
    }
  };

  return (
    <Container fluid className="json-editor">
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        id="editor-tabs"
        className="justify-content-center"
      >
        <Tab eventKey="editing" title="Editing">
          <div
            className={`editor-content ${isNodeSelected ? "" : "full-width"}`}
          >
            <div className="tree-container">
              <div className="editor-buttons">
                <Button
                  variant="warning"
                  onClick={handleGenerateAllDescriptionsAndValidations}
                  disabled={isGenerating}
                  style={{ marginRight: "10px" }}
                >
                  {isGenerating
                    ? "Generating for All Images..."
                    : "Generate All"}
                </Button>
                <Button
                  variant="success"
                  onClick={handleSaveAll}
                  disabled={isGenerating}
                  style={{ marginRight: "10px" }}
                >
                  {isGenerating ? "Processing..." : "Save File"}
                </Button>
                {isReportReady && (
                  <Button
                    variant="info"
                    onClick={navigateToReport}
                    style={{ marginTop: "5px", marginLeft: "20px" }}
                  >
                    View Report
                  </Button>
                )}
              </div>
              <div className="tree-view">
              {loading ? (
                  <div>Loading...</div> // Show a loading indicator
                ) : jsonData && jsonData.children && jsonData.children.length > 0 ? (
                  <Tree
                    data={jsonData}
                    orientation="horizontal"
                    translate={{ x: 200, y: 100 }}
                    nodeLabelComponent={{
                      render: <>{renderNodeLabel}</>,
                      foreignObjectWrapper: {
                        y: -20,
                        x: -20,
                      },
                    }}
                    onNodeClick={({ data }) => handleClick(data)}
                  />
                ) : (
                  <div>No valid data to display</div>
                )}
              </div>
            </div>
            {isNodeSelected && (
              <div className="editor-panel">
                <div className="section meta-data">
                  <h5>Meta Data</h5>
                  <div className="section-content">
                    <div className="left-half">
                      <p>
                        <strong>URL:</strong>{" "}
                        <span title={formData.url}>
                          {truncateURL(formData.url)}
                        </span>
                      </p>
                      <p>
                        <strong>Action:</strong> {formData.action}
                      </p>
                    </div>
                    <div className="right-half">
                      {formData.image_url ? (
                        <img
                          src={formData.image_url}
                          alt="Preview"
                          className="image-preview"
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowModal(true)} // Open modal on image click
                        />
                      ) : (
                        <div className="no-image-placeholder">
                          No image captured on this URL
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="section information">
                  <h5>Information</h5>
                  <Tabs defaultActiveKey="description" id="information-tabs">
                    <Tab eventKey="description" title="Description">
                      <Form.Control
                        as="textarea"
                        rows={6}
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                      />
                      <Button
                        variant="secondary"
                        onClick={handleRegenerateDescription}
                        className="mt-2"
                        disabled={isGenerating}
                      >
                        {isGenerating
                          ? "Processing..."
                          : "Generate Description"}
                      </Button>
                    </Tab>
                    <Tab eventKey="validation" title="Validation">
                      <Form.Control
                        as="textarea"
                        rows={6}
                        name="validation"
                        value={formData.validation}
                        onChange={handleInputChange}
                      />
                      <Button
                        variant="secondary"
                        onClick={handleRegenerateValidation}
                        className="mt-2"
                        disabled={isGenerating}
                      >
                        {isGenerating ? "Processing..." : "Generate Validation"}
                      </Button>
                    </Tab>
                  </Tabs>
                </div>

                <div className="section agents">
                  <h5>Agents</h5>
                  <h6>
                    Agents Suggestions = [
                    {selectedNode && selectedNode.agents_recommendation ? (
                      selectedNode.agents_recommendation.map((agent, index) => (
                        <span key={agent}>
                          {AGENT_FUNCTIONS[agent] || agent}
                          {index < selectedNode.agents_recommendation.length - 1
                            ? ", "
                            : ""}
                        </span>
                      ))
                    ) : (
                      <span>Getting Suggestions...</span> // Show this while fetching suggestions
                    )}
                    ]
                  </h6>
                  <Row>
                    <Col md={6}>
                      <DropdownButton
                        id="dropdown-agent-selection"
                        title="Select Agents"
                        onSelect={(agentKey) => handleAgentSelect(agentKey)}
                      >
                        <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                          <Form>
                            <FormCheck
                              label="Select All"
                              onChange={() => {
                                const allAgents = Object.keys(
                                  AGENT_FUNCTIONS
                                ).filter(
                                  (key) => key !== "meta_deceptive_design" // Exclude "I Don't Know"
                                );
                                setSelectedAgents(allAgents);
                              }}
                              checked={
                                selectedAgents.length ===
                                Object.keys(AGENT_FUNCTIONS).filter(
                                  (key) => key !== "meta_deceptive_design"
                                ).length
                              }
                            />
                            {Object.keys(AGENT_FUNCTIONS).map((agentKey) => (
                              <FormCheck
                                key={agentKey}
                                label={AGENT_FUNCTIONS[agentKey]}
                                onChange={() => handleAgentSelect(agentKey)}
                                checked={selectedAgents.includes(agentKey)} // Ensure it shows selected agents
                              />
                            ))}
                          </Form>
                        </div>
                      </DropdownButton>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Add Description?</Form.Label>
                        <Form.Check
                          type="radio"
                          label="Yes"
                          name="descriptionOption"
                          id="useCurrent"
                          checked={useCurrentDescription}
                          onChange={() => setUseCurrentDescription(true)}
                        />
                        <Form.Check
                          type="radio"
                          label="No"
                          name="descriptionOption"
                          id="useDefault"
                          checked={!useCurrentDescription}
                          onChange={() => setUseCurrentDescription(false)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Button
                        variant="primary"
                        onClick={handleGenerateAgentResponses}
                        disabled={isGenerating}
                        className="mt-2"
                      >
                        {isGenerating
                          ? "Processing..."
                          : "Generate Agent Responses"}
                      </Button>
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col md={12}>
                      <Form.Label>View/Edit Agent Responses</Form.Label>
                      <DropdownButton
                        id="dropdown-agent-response"
                        title={
                          selectedAgentKey
                            ? AGENT_FUNCTIONS[selectedAgentKey] || "All"
                            : "View Agents"
                        }
                        onSelect={handleAgentViewChange}
                      >
                        <Dropdown.Item eventKey="All">All</Dropdown.Item>
                        {Object.keys(formData.agents).map((agentKey) => (
                          <Dropdown.Item key={agentKey} eventKey={agentKey}>
                            {AGENT_FUNCTIONS[agentKey]}
                          </Dropdown.Item>
                        ))}
                      </DropdownButton>
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col md={6}>
                      <Form.Label>Agent Response</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        value={
                          selectedAgentKey === "ALL"
                            ? formData.agents["ALL"]?.result || ""
                            : formData.agents[selectedAgentKey]?.result || ""
                        }
                        onChange={handleAgentInputChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label>Agent Validation</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        value={
                          selectedAgentKey === "ALL"
                            ? formData.agents["ALL"]?.validation || ""
                            : formData.agents[selectedAgentKey]?.validation ||
                              ""
                        }
                        readOnly
                      />
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md={6}>
                      <Form.Label>Feedback for Agent</Form.Label>
                      <Form.Control
                        as="select"
                        onChange={(e) =>
                          setFormData({ ...formData, feedback: e.target.value })
                        }
                      >
                        <option value="">Select Feedback</option>
                        <option value="Perfect">Perfect</option>
                        <option value="Slightly misses the mark">
                          Slightly misses the mark
                        </option>
                        <option value="Off track">Off track</option>
                      </Form.Control>
                    </Col>

                    {/* Display Text Area if Feedback is "Slightly misses the mark" or "Off track" */}
                    {formData.feedback === "Slightly misses the mark" ||
                    formData.feedback === "Off track" ? (
                      <Col md={6}>
                        <Form.Label>Feedback Details</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={formData.feedbackDetails}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              feedbackDetails: e.target.value,
                            })
                          }
                          placeholder="Provide detailed feedback"
                        />
                      </Col>
                    ) : null}
                  </Row>

                  <Button
                    variant="primary"
                    onClick={handleSubmitFeedback}
                    className="mt-3"
                    disabled={
                      !formData.feedback ||
                      (formData.feedback !== "Perfect" &&
                        !formData.feedbackDetails)
                    }
                  >
                    Submit Feedback
                  </Button>
                  {updateMessage && (
                    <p className="update-message">{updateMessage}</p>
                  )}
                </div>

                <div className="section suggestions">
                  <h5>Outcomes and Feedback</h5>
                  <div className="section-content">
                    <div className="left-half">
                      <Form.Label>Outcomes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        name="suggestion"
                        value={formData.suggestion}
                        onChange={handleInputChange}
                      />
                      <Button
                        variant="primary"
                        onClick={handleGenerateSuggestions}
                        className="mt-2"
                        disabled={isGenerating}
                      >
                        {isGenerating
                          ? "Generating..."
                          : "Generate Suggestions"}
                      </Button>
                    </div>
                    <div className="right-half">
                      <Form.Label>Feedback Image</Form.Label>
                      {formData.feedback_image_url && (
                        <img
                          src={formData.feedback_image_url}
                          alt="Feedback"
                          className="image-preview"
                        />
                      )}
                    </div>
                  </div>
                </div>

                <Button
                  variant="primary"
                  onClick={handleSaveNode}
                  className="mt-3"
                >
                  Save Node
                </Button>
                {saveMessage && <p>{saveMessage}</p>}
              </div>
            )}
          </div>
          {errorMessage && (
            <Alert variant="danger" className="mt-3">
              {errorMessage}
            </Alert>
          )}
        </Tab>
        <Tab eventKey="jsonView" title="View">
          {/* Button to send JSON to iframe */}
          <div className="d-flex justify-content-center mt-3">
            <Button onClick={sendToEmbed} variant="primary">
              Load View
            </Button>
          </div>

          {/* Define the iframe here */}
          <iframe
            ref={iframeRef}
            id="jsoncrackEmbed"
            src="https://jsoncrack.com/widget"
            style={{
              width: "100%",
              height: "500px",
              border: "none",
              marginTop: "20px",
            }}
            title="JSON Crack"
          />
        </Tab>
        <Tab eventKey="userJourney" title="User Journey">
          {userJourneyError ? (
            <div>{userJourneyError}</div>
          ) : !userJourneyData ? (
            <div>Loading user journey...</div>
          ) : (
            <div style={{ width: "100%", height: "600px" }}>
              <ReactFlowProvider>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  nodeTypes={nodeTypes}
                  fitView
                  attributionPosition="bottom-left"
                >
                  <Controls />
                  <Background gap={16} />
                </ReactFlow>
              </ReactFlowProvider>
            </div>
          )}
        </Tab>
        <Tab eventKey="flowChart" title="Flow Chart">
          {/* FlowChart component that handles the visualization */}
          <FlowChart jsonData={jsonData} userJourneyData={userJourneyData} />
        </Tab>
      </Tabs>
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <img
            src={formData.image_url}
            alt="Enlarged Preview"
            style={{ width: "100%" }}
          />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default JsonEditor;
